import { Http } from './http.init'
import { ErrorWrapper, ResponseWrapper } from './http.execution'

export class FooshiApi {
  static async logIn (loginInfo) {
    try {
      const response = await new Http({ auth: false }).post(`auth/login`, loginInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async forgotPassword (forgotPasswordInfo) {
    try {
      const response = await new Http({ auth: false }).post(`auth/forgot-password`, forgotPasswordInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async resetPassword (resetPasswordInfo) {
    try {
      const response = await new Http({ auth: false }).post(`auth/reset-password`, resetPasswordInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async signUp (signUpInfo) {
    try {
      const response = await new Http({ auth: false }).post(`auth/signup`, signUpInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async addAccountDetails (accountDetails) {
    try {
      const response = await new Http({ auth: true }).post(`onboarding/vendor-setup`, accountDetails)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async addAccountOwner (accountOwner) {
    try {
      const response = await new Http({ auth: true }).post(`account/owner`, accountOwner)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addTermsAndConditions () {
    try {
      const response = await new Http({ auth: true }).post(`account/terms-and-conditions`, {})
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getSession (id) {
    try {
      const response = await new Http({ auth: true }).get(`session/${id}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getUser () {
    try {
      const response = await new Http({ auth: true }).get(`users`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getCountries ({ includeAll }) {
    return FooshiApi.get(`countries`, { includeAll })
  }

  static async getProductProperties () {
    return FooshiApi.get(`product-properties`)
  }

  static async profile () {
    try {
      const response = await new Http({ auth: true }).get(`account/profile`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error, 'Failed to fetch user profile')
    }
  }

  static async get (endpoint, params) {
    try {
      const response = await new Http({ auth: true }).get(endpoint, { params })
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  // portal
  static async getAllProducts (filter) {
    return FooshiApi.get(`products`, filter)
  }

  static async uploadDocument (file) {
    try {
      const formData = new FormData()
      formData.append('document', file)
      const response = await new Http({ auth: true }).post(`products/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async getUploadDocumentStatus (batchUploadId) {
    try {
      const response = await new Http({ auth: true }).get(`products/status-document/${batchUploadId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async updateAllProducts (update) {
    try {
      const response = await new Http({ auth: true }).post(`products/batch/update`, update)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addProduct (product) {
    try {
      const response = await new Http({ auth: true }).post(`products`, product)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async updateProduct (product) {
    try {
      const response = await new Http({ auth: true }).put(`products/${product.id}`, product)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async removeProduct (productId) {
    try {
      const response = await new Http({ auth: true }).delete(`products/${productId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getProduct (productId) {
    try {
      const response = await new Http({ auth: true }).get(`products/${productId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addAccountOwnerDetails (ownerInfo) {
    try {
      const response = await new Http({ auth: true }).put(`users`, ownerInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getPaymentAccount () {
    try {
      const response = await new Http({ auth: true }).get(`payments/account/`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async createPaymentAccount () {
    try {
      const response = await new Http({ auth: true }).post(`payments/account/`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getVendorShippingMethods () {
    try {
      const response = await new Http({ auth: true }).get(`shippings`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addVendorShippingMethods (shippingMethods) {
    try {
      const response = await new Http({ auth: true }).post(`shippings`, shippingMethods)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async deactivateVendorShippingMethods (shippingToCountry) {
    try {
      const response = await new Http({ auth: true }).delete(`shippings/${shippingToCountry}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async stripeOboardingLink () {
    try {
      const response = await new Http({ auth: true }).get(`payments/onboarding/`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getVendor () {
    try {
      const response = await new Http({ auth: true }).get(`vendors`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async updateVendor (vendorInfo) {
    try {
      const response = await new Http({ auth: true }).put(`vendors`, vendorInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async updateUser (UserInfo) {
    try {
      const response = await new Http({ auth: true }).put(`users`, UserInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getProductImage (imageId) {
    try {
      const response = await new Http({ auth: true }).get(`product-image/${imageId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async changePassword (PasswordInfo) {
    try {
      const response = await new Http({ auth: true }).post(`auth/change-password`, PasswordInfo)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async buildCatalogue (data) {
    try {
      const response = await new Http({ auth: true }).post(`onboarding/build-catalogue`, data)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async getOnboardingStatus () {
    try {
      const response = await new Http({ auth: true }).get(`onboarding/status`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getOrder (orderId) {
    try {
      const response = await new Http({ auth: true }).get(`orders/${orderId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getAllOrders () {
    try {
      const response = await new Http({ auth: true }).get(`orders`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getOrderStatus (orderId) {
    try {
      const response = await new Http({ auth: true }).get(`orders/status/${orderId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async acceptOrder (orderId) {
    try {
      const response = await new Http({ auth: true }).put(`orders/accept/${orderId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async declineOrder (orderId) {
    try {
      const response = await new Http({ auth: true }).put(`orders/decline/${orderId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addTracking (orderId, shippingTracking) {
    try {
      const response = await new Http({ auth: true }).put(`orders/tracking/${orderId}`, shippingTracking)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getOrderCountByStatus () {
    try {
      const response = await new Http({ auth: true }).get(`orders/count/`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getContainers () {
    try {
      const response = await new Http({ auth: true }).get(`containers`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async addContainer (container) {
    try {
      const response = await new Http({ auth: true }).post(`containers`, container)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      return new ErrorWrapper(error)
    }
  }

  static async deleteContainer (containerId) {
    try {
      const response = await new Http({ auth: true }).delete(`containers/${containerId}`)
      return new ResponseWrapper(response, response.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/fooshiApi.service.js